import { useEffect, useRef } from 'react';
import { loadAccountingLists, loadOrderReferenceLists } from '../utils/orderReferenceLists';

/**
 * THE expanded order row's reference-list loading, for BOTH expansion hooks —
 * `useMyOrderExpansion` and `useDeptOrderExpansion`.
 *
 * Extracted 2026-10-03, in the same pass that put the fetches themselves in
 * `utils/orderReferenceLists.js`. Sharing only the fetches left these two
 * effect SHELLS *byte-identical* between the two hooks (`check-duplication`
 * scored both at similarity **1.00**, 36 and 26 normalised tokens) — and the
 * house rule is that a manifest entry is debt, not a fix. So the shells, and
 * the latch they both read, live here once.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🚨 THREE EXPORTS RATHER THAN ONE, AND THE REASON IS A MEASUREMENT
 * ═══════════════════════════════════════════════════════════════════════════
 * The obvious shape — one hook owning the latch and both effects — was built
 * first and **the parity goldens rejected it.** React runs effects in
 * declaration order, so effect order IS request order, and the two hooks
 * disagreed about it: Department Orders loaded the accounting lists first, My
 * Orders loaded the reference data first. One fixed order inside one hook
 * therefore had to change one of them.
 *
 * 🔻 **MEASURED, NOT REASONED.** With a single hook fixing accounting-first,
 * **four `MyOrders.parityGolden` states went red**, because those goldens record
 * the whole recorded CALL LIST and not merely its length — the diff was
 * `/api/productions` where `/api/budget/cost-codes/active?department_id=1` now
 * stood, seven lines shifted. A golden diff is a stop-and-report, never a
 * re-baseline, so the fix was to keep each caller's order rather than to
 * re-capture.
 *
 * 🔑 **SO THE ORDER LIVES AT THE CALL SITE, WHERE IT IS VISIBLE.** Each caller
 * calls `useOrderReferenceArmed` and then the two list hooks **in the order it
 * already used**; hook call order is effect declaration order is request order.
 * That beats an `order: 'accountingFirst' | 'referenceFirst'` parameter (the
 * other candidate) on two counts: it needs no conditional dependency array, and
 * a reader of `useMyOrderExpansion` can SEE which load goes first instead of
 * decoding a string.
 * 🚫 **Do not "tidy" the three back into one.** The goldens will go red, and the
 * thing that looks like tidying is a behaviour change to the request sequence.
 *
 * ⚠️ These own NO list state: every setter is passed in, because the lists live
 * in the calling hook beside the rest of its expansion state. They resolve no
 * authority either — `pages/NewOrder.js`'s capability gate has no equivalent
 * here, and neither hook had one.
 */

/**
 * THE `deferReferenceData` latch — ONE copy, and it is a LATCH, not a flag.
 *
 * The lists feed ONLY the expanded row's inline editor. An order page wants them
 * on mount (`deferReferenceData` default `false`); the hires Table
 * (`components/hires/HireOrderExpansion.js`) mounts an expansion hook for a
 * table that is usually never expanded, so it opts in and the lists load on the
 * FIRST expand — **once, never re-armed**.
 *
 * 🪤 **It is a `useRef` written during RENDER, deliberately, and it must stay
 * that way.** Arming it in an effect would cost an extra render before the first
 * fetch could be issued, so the first expand would paint its editor against
 * empty pickers. Once armed it stays armed for the hook's life, which is what
 * stops a collapse-then-expand re-issuing all seven requests.
 */
export function useOrderReferenceArmed({ deferReferenceData = false, expandedOrders }) {
  const armedRef = useRef(!deferReferenceData);
  if (expandedOrders && expandedOrders.size > 0) armedRef.current = true;
  return armedRef.current;
}

/**
 * The PER-DEPARTMENT pair: cost codes + payment types. Refetches on a switch,
 * and an absent department CLEARS both lists AND both failure flags.
 *
 * 🔑 AN EMPTIED OPTION LIST IS NOT A COSMETIC LOSS, and why a non-ok response
 * must throw so a refusal and a rejection take the same path, are in
 * `utils/orderReferenceLists.js` (rule (a)). The two lists keep SEPARATE
 * failure keys: two requests, either refusable alone.
 */
export function useOrderAccountingLists({
  referenceArmed,
  activeDepartment,
  setCostCodes,
  setPaymentTypes,
  setLoadFailures,
}) {
  useEffect(() => {
    if (!referenceArmed) return;
    loadAccountingLists({
      departmentId: activeDepartment ? activeDepartment.department_id : null,
      entries: [
        { setList: setCostCodes, failureKey: 'costCodes' },
        { setList: setPaymentTypes, failureKey: 'paymentTypes' },
      ],
      setLoadFailures,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDepartment, referenceArmed]);
}

/**
 * The ONCE-PER-PAGE five: productions, supplier records, sub-departments, gags
 * and the supplier-name suggestions.
 *
 * 🚨 **A SEPARATE EFFECT FROM THE PAIR ABOVE BECAUSE THE DEPENDENCIES DIFFER** —
 * these do not vary with the active department (the server scopes each list to
 * the caller), so they load once. Folding them into the accounting effect would
 * refetch all five on every department switch.
 *
 * 🪤 The `null` supplier sentinel, why `[]` cannot serve as one, and why each
 * supplier entry catches for ITSELF, are in `utils/orderReferenceLists.js`
 * (rule (b)). Both expansion hooks are FIVE-list callers — passing
 * `setSupplierSuggestions` is what makes that true; `pages/Approvals.js` omits
 * it and issues no fifth request.
 */
export function useOrderReferenceData({
  referenceArmed,
  setProductions,
  setSuppliers,
  setSubDepartments,
  setGags,
  setSupplierSuggestions,
  setLoadFailures,
}) {
  useEffect(() => {
    if (!referenceArmed) return;
    loadOrderReferenceLists({
      setProductions,
      setSuppliers,
      setSubDepartments,
      setGags,
      setSupplierSuggestions,
      setLoadFailures,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceArmed]);
}
